import { CSSProperties, type ComponentType, ReactNode, useState } from "react";
import clsx from "clsx";
import { cn } from "@/core/helpers/cn";
import useClickAway from "@/core/helpers/useClickAway";

type Props = {
  value?: ReactNode;
  placeholder?: string;
  className?: string;
  classNameSelect?: string;
  classNamePlaceholder?: string;
  classNameIcon?: string;
  classNameOptopns?: string;
  Icon?: ComponentType<{ className?: string }>;
  children?: ReactNode;
  position?: "left" | "right";
  optionsStyle?: CSSProperties;
  bg?: string;
};

export default function Select({
  value,
  placeholder,
  className,
  classNameSelect,
  classNamePlaceholder,
  classNameIcon,
  classNameOptopns,
  Icon,
  children,
  position = "left",
  optionsStyle,
  bg,
}: Props) {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useClickAway(() => setIsOpen(false));

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div
      ref={ref}
      className={clsx("min-h-10.5 h-10.5 relative cursor-pointer text-[0.875rem]", className)}
      onClick={(e) => {
        e.preventDefault();
        setIsOpen((state) => !state);
      }}
    >
      <div
        className={cn(
          "relative px-4 h-full flex items-center justify-between bg-dark-800",
          bg,
          isOpen ? "rounded-t-[0.375rem]" : "rounded-[0.375rem]",
          classNameSelect,
        )}
      >
        {value || (
          <div className={cn("text-white/50", classNamePlaceholder)}>{placeholder || ""}</div>
        )}

        {Icon ? (
          <Icon
            className={cn(
              "absolute right-2 transition-transform",
              isOpen && "rotate-180",
              classNameIcon,
            )}
          />
        ) : (
          <div
            className={clsx(
              "absolute text-[0.5rem] right-6 transition-transform font-arial text-white",
              isOpen ? "" : "rotate-180",
            )}
          >
            &#9650;
          </div>
        )}
      </div>

      {isOpen && (
        <div
          className={cn(
            "absolute w-full dark:bg-dark-800 bg-[#f1f1f1] rounded-b-[0.375rem]",
            position === "left" && "right-0",
            position === "right" && "left-0",
            bg,
            classNameOptopns,
          )}
          style={{
            boxShadow: "0px 10px 15px 0px #121212",
            ...optionsStyle,
          }}
        >
          {children}
        </div>
      )}
    </div>
  );
}
